import React from 'react';

/**
 * Catches render/lifecycle errors anywhere below it.
 *
 * Without a boundary, React unmounts the entire tree on an unhandled error and
 * leaves a blank white page - the whole app looks broken because of a bug on one
 * screen. This keeps the failure contained and, importantly, does NOT touch the
 * stored session, so recovering is a reload rather than a fresh sign-in.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the details in the console for diagnosis.
    console.error('Unhandled UI error:', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 max-w-lg w-full">
          <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-amber-50 text-amber-600 shrink-0">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-gray-800 mb-1">
                Something went wrong on this page
              </h1>
              <p className="text-sm text-gray-600">
                You are still signed in. Reloading usually fixes it &mdash; the rest
                of the app is unaffected.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-6">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-[#0077B6] text-white text-sm font-semibold hover:bg-[#005a8b] transition-colors"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.assign('/dashboard')}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Back to dashboard
            </button>
          </div>

          {process.env.NODE_ENV !== 'production' && (
            <pre className="mt-6 p-3 rounded-lg bg-gray-900 text-gray-100 text-[11px] overflow-x-auto whitespace-pre-wrap">
              {String(error?.stack || error?.message || error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
